import { useState, useEffect, useCallback, Dispatch as DispatchReact, SetStateAction } from 'react';
import equal from 'fast-deep-equal';

import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { fieldsFillCounter } from 'CorePackage/MainModule/common/fieldsFillCounter';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';

import { IFilterExtraField } from '../interfaces/IFilterExtraField';
import { IFilterWithMeta } from '../interfaces/IFilterWithMeta';
import { IRenderFormContent } from '../interfaces/IRenderFormContent';
import { ISetFilterCurrentParams } from '../interfaces/ISetFilterCurrentParams';
import { FiltersForm } from './FiltersForm';
import { FilterNameEditor } from './FilterNameEditor';
import { FiltersModalNavigation } from './FiltersModalNavigation';
import { SelectTable } from './SelectTable';
import styles from './FiltersModal.module.scss';
import { FormErrors } from '../../../KnmPackage/MainModule/types/FormErrors';

const FILTER_NAME_NEW_TEXT = 'Новый фильтр';

interface IFiltersModalProps<FormValues, Filter> {
  visible: boolean;
  initialFormValues: FormValues;
  additionalFields: IFilterExtraField[];
  filters: IFilterWithMeta<Filter>[];
  currentFilter: Filter | undefined;
  activeFilterId: number | undefined;
  renderFormContent: IRenderFormContent<FormValues>;
  mapFilter: (values: FormValues, filterName: string) => Filter;
  mapFilterAdditionalFields: (filter: Filter) => IFilterExtraField[];
  onClose: () => void;
  onTemplateCreate: () => void;
  setFormValues: DispatchReact<SetStateAction<FormValues | Filter>>;
  setAdditionalFields: DispatchReact<SetStateAction<IFilterExtraField[]>>;
  getData: () => void;
  getFilterData: (id: IFilterResult['id']) => void;
  setFilterCurrent: (params: ISetFilterCurrentParams<Filter>) => void;
  setFilterSwitcher: (value: boolean) => void;
  createFilter: (data: Filter) => void;
  updateFilter: (id: IFilterResult['id'], data: Filter) => void;
  removeFilter: (id: IFilterResult['id']) => void;
  formValidate?: (values: FormValues) => FormErrors<FormValues>;
  excludedCounterFields?: string[];
}

export function FiltersModal<FormValues, Filter extends { name: string; isTemplateFirstCreate?: boolean }>({
  visible,
  initialFormValues,
  additionalFields,
  filters,
  currentFilter,
  activeFilterId,
  renderFormContent,
  mapFilter,
  mapFilterAdditionalFields,
  onClose,
  onTemplateCreate,
  setFormValues,
  setAdditionalFields,
  getData,
  getFilterData,
  setFilterCurrent,
  setFilterSwitcher,
  createFilter,
  updateFilter,
  removeFilter,
  formValidate,
  excludedCounterFields,
}: IFiltersModalProps<FormValues, Filter>) {
  const [activeTab, setActiveTab] = useState<'main' | 'add'>('main');
  const [filterNameInputValue, setFilterNameInputValue] = useState(FILTER_NAME_NEW_TEXT);
  const [filterNameInputError, setFilterNameInputError] = useState('');
  const [fieldsFillCount, setFieldsFillCount] = useState(0);
  const [activeTemplateId, setActiveTemplateId] = useState<IFilterResult['id'] | undefined>(activeFilterId);
  const [currentAdditionalFields, setCurrentAdditionalFields] = useState(additionalFields);

  useEffect(() => {
    setCurrentAdditionalFields(additionalFields);
  }, [additionalFields]);

  useEffect(() => {
    const filterData: Filter | undefined = filters.find(filter => filter.meta.id === activeTemplateId)?.data;

    if (filterData) {
      setFormValues(filterData);
      setAdditionalFields(mapFilterAdditionalFields(filterData));
      setFilterNameInputValue(filterData.name);
    }
  }, [activeTemplateId, filters, mapFilterAdditionalFields, setAdditionalFields, setFormValues]);

  const fieldsCounter = useCallback(
    (data: FormValues) => fieldsFillCounter(data, setFieldsFillCount, excludedCounterFields),
    [],
  );

  const onActiveTemplateChange = useCallback(
    (id: IFilterResult['id']) => {
      setActiveTemplateId(id);
      getFilterData(id);
    },
    [getFilterData],
  );

  const handleTemplateCreate = useCallback(() => {
    setActiveTemplateId(undefined);
    setFilterNameInputValue(FILTER_NAME_NEW_TEXT);
    if (onTemplateCreate) {
      onTemplateCreate();
    }
  }, [onTemplateCreate]);

  useEffect(() => {
    if (currentFilter === undefined) {
      handleTemplateCreate();
    } else {
      setActiveTemplateId(activeFilterId);
      setFormValues(currentFilter);
      setFilterNameInputValue(currentFilter.name);
      setAdditionalFields(mapFilterAdditionalFields(currentFilter));
    }
  }, [currentFilter, activeFilterId, setFormValues, setAdditionalFields]);

  const handleAfterClose = useCallback(() => setActiveTab('main'), []);

  const handleFormSubmit = useCallback(
    (data: FormValues) => {
      if (filterNameInputError) {
        return;
      }
      const filter = mapFilter(data, filterNameInputValue);
      const templateFilter = filters.find(f => f.meta.id === activeTemplateId)?.data;
      const filterData: ISetFilterCurrentParams<Filter> = {
        current: filter,
        currentId: activeTemplateId,
      };

      if (!equal(filter, templateFilter)) {
        setFilterNameInputValue(FILTER_NAME_NEW_TEXT);
        setActiveTemplateId(undefined);
        const newFilter: Filter = {
          ...filter,
          name: FILTER_NAME_NEW_TEXT,
        };

        setFormValues(newFilter);
        filterData.current = newFilter;
        filterData.currentId = undefined;
      }
      setFilterCurrent({
        ...filterData,
        current: filterData?.current
          ? deleteObjectProperties<Filter, 'isTemplateFirstCreate'>(filterData.current, ['isTemplateFirstCreate'])
          : undefined,
      });
      setFilterSwitcher(true);
      getData();
      onClose();
    },
    [
      filterNameInputError,
      mapFilter,
      filterNameInputValue,
      filters,
      activeTemplateId,
      setFilterCurrent,
      setFilterSwitcher,
      getData,
      onClose,
      setFormValues,
    ],
  );

  const saveFilter = useCallback(
    (data: FormValues) => {
      const existedFilter = filters?.find(filter => filter.meta.name === filterNameInputValue);

      if (existedFilter && activeTemplateId !== existedFilter?.meta.id) {
        setFilterNameInputError('Фильтр с таким именем уже существует');

        return;
      }
      const filter = mapFilter(data, filterNameInputValue);

      if (activeTemplateId) {
        updateFilter(activeTemplateId, filter);
      } else {
        createFilter(filter);
      }
    },
    [activeTemplateId, createFilter, filterNameInputValue, filters, mapFilter, updateFilter],
  );

  const handleApply = useCallback(() => {
    setAdditionalFields(currentAdditionalFields);
    setActiveTab('main');
  }, [currentAdditionalFields, setAdditionalFields]);

  const handleCancel = useCallback(() => {
    setCurrentAdditionalFields(additionalFields);
    setActiveTab('main');
  }, [additionalFields]);

  return (
    <Modal visible={visible} onClose={onClose} afterClose={handleAfterClose}>
      {activeTab === 'main' && (
        <div className={styles.Main}>
          <FiltersModalNavigation<Filter>
            filters={filters}
            newFilterName={filterNameInputValue}
            activeTemplateId={activeTemplateId}
            fieldsFillCount={fieldsFillCount}
            onActiveTemplateChange={onActiveTemplateChange}
            onTemplateCreate={handleTemplateCreate}
            removeFilter={removeFilter}
          />
          <div className={styles.FilterContent}>
            <FilterNameEditor
              filterName={filterNameInputValue}
              setFilterName={setFilterNameInputValue}
              filterNameInputError={filterNameInputError}
              setFilterNameInputError={setFilterNameInputError}
            />
            <FiltersForm<FormValues>
              initialValues={initialFormValues}
              filterName={filterNameInputValue}
              saveFilter={saveFilter}
              setFormValues={setFormValues}
              fieldsCounter={fieldsCounter}
              renderContent={renderFormContent}
              handleFormSubmit={handleFormSubmit}
              setActiveTab={setActiveTab}
              formValidate={formValidate}
            />
          </div>
        </div>
      )}
      {activeTab === 'add' && (
        <div className={styles.Add}>
          <ModalHeader type="additional" color="black" title="Добавить поле" />
          <SelectTable items={currentAdditionalFields} onItemSet={setCurrentAdditionalFields} />
          <div className={styles.FilterFooterButtons}>
            <ModalActions placement="left" padding="none">
              <Button text="Применить" color="primary" onClick={handleApply} />
              <Button text="Отменить" variant="linkButton" onClick={handleCancel} />
            </ModalActions>
          </div>
        </div>
      )}
    </Modal>
  );
}
